/**
 * ThriftService
 * Sends SIFT classification results and the source image to the SIFT backend,
 * which proxies the request to the THRIFT store using a server-side API key.
 * Admin-only feature — the backend enforces this via the require_admin dependency.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { AuthService } from './AuthService';
import config from '../config';
import type { ClassificationResult } from '../types';

export interface ThriftPushResult {
  status: string;
}

export class ThriftService {
  private axiosInstance: AxiosInstance;
  private authService: AuthService;

  constructor() {
    this.authService = AuthService.getInstance();

    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 35000,
    });

    // Attach JWT token to every request
    this.axiosInstance.interceptors.request.use(
      async (cfg) => {
        try {
          const token = await this.authService.getValidAccessToken();
          if (token) {
            cfg.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('ThriftService: failed to get access token:', error);
        }
        return cfg;
      },
      (error) => Promise.reject(error)
    );
  }

  /**
   * Push a SIFT classification result and the original images to THRIFT via
   * the SIFT backend proxy.  The THRIFT API key never leaves the server.
   *
   * @param results    ClassificationResult array from the /classify response
   * @param imageBlobs The original source images captured before SIFT submission
   */
  async push(results: ClassificationResult[], imageBlobs: Blob[]): Promise<ThriftPushResult> {
    const formData = new FormData();
    formData.append('result', JSON.stringify(results));
    imageBlobs.forEach((blob, i) => {
      formData.append('photos', blob, `garment_${i}.jpg`);
    });

    try {
      const response = await this.axiosInstance.post<ThriftPushResult>(
        '/api/thrift/push',
        formData,
        // Let the browser set Content-Type with the correct multipart boundary
        { headers: { 'Content-Type': undefined } }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{ detail?: string }>;
      if (axiosError.response) {
        const status = axiosError.response.status;
        const detail = axiosError.response.data?.detail;
        if (status === 401) return new Error('Authentication failed. Please log in again.');
        if (status === 402) return new Error('Insufficient credits. Push to THRIFT requires 5 credits per image for AI image generation.');
        if (status === 403) return new Error('THRIFT account not linked. Link your account on the Account page first.');
        if (status === 503) return new Error('THRIFT integration is not configured on the server.');
        if (status === 504) return new Error('THRIFT request timed out. Please try again.');
        if (status === 502) return new Error('Could not reach THRIFT. Please try again.');
        return new Error(detail ?? `THRIFT push failed (${status})`);
      }
      if (axiosError.code === 'ECONNABORTED') return new Error('Request timed out.');
      return new Error('Network error. Please check your connection.');
    }
    if (error instanceof Error) return error;
    return new Error('Failed to push to THRIFT');
  }
}

export default ThriftService;
